import * as Sentry from '@sentry/nextjs';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useErrorsMutation } from 'hooks/api/useErrorMutation';
import { StripePaymentValues, useStripePaymentMutation } from 'hooks/stripe/useStripePaymentMutation';
import { useState } from 'react';
import { MakeStripePayment } from '../../components/core/stripe/StripeField/types';
import { UserRoleIDEnum } from '@alluradirect/react';

export interface UseStripePaymentArgs {
  userId?: number;
  userTypeId?: UserRoleIDEnum;
}

export const useStripePayment = ({ userId = 0, userTypeId = UserRoleIDEnum.guest }: UseStripePaymentArgs = {}) => {
  const stripe = useStripe();
  const elements = useElements();
  const errorsMutation = useErrorsMutation();
  const [isLoading, setIsLoading] = useState(false);

  const { isError: isAlluraServerError, mutateAsync: makePayment } = useStripePaymentMutation();

  const makeStripePayment: MakeStripePayment = async ({
    cardHolder,
    endpoint,
    onSuccess,
    onStripeError,
    onSettled,
    additionalSendData,
    sendWithoutCardData,
  }) => {
    setIsLoading(true);
    const cardElement = elements?.getElement(CardElement);
    if (!cardElement && !sendWithoutCardData) throw new Error('Could not get CardElement');

    try {
      let stripeToken: any = null;
      if (!sendWithoutCardData) {
        if (cardElement) {
          stripeToken = await stripe?.createToken(cardElement, cardHolder);
          if (!stripeToken) throw new Error('Could not create Stripe Token');
        }
      }

      if (stripeToken?.error) {
        throw new Error(JSON.stringify(stripeToken.error));
      }

      if (!stripeToken?.token?.card && !sendWithoutCardData) {
        throw new Error('stripeToken.token.card did not return anything');
      }

      const values: StripePaymentValues = {
        endpoint,
        data: {
          stripe_credit_card_brand: stripeToken?.token?.card?.brand ?? '',
          stripe_credit_card_country: stripeToken?.token?.card?.country ?? '',
          stripe_credit_card_exp_month: stripeToken?.token?.card?.exp_month ?? 0,
          stripe_credit_card_exp_year: stripeToken?.token?.card?.exp_year ?? 0,
          stripe_credit_card_last4: stripeToken?.token?.card?.last4 ?? '',
          stripe_credit_card_token: stripeToken?.token?.id,
          stripe_credit_card: '',
          cardholder_name: stripeToken?.token?.card?.name ?? '',
        },
        additionalSendData,
      };

      await makePayment(values, {
        onSuccess: (data: any) => {
          if (!data?.SUCCESS) {
            onStripeError(data);
          } else {
            onSuccess(data);
          }
        },
        onSettled,
      });
    } catch (e: any) {
      console.error(e);
      setIsLoading(false);
      Sentry.captureException(e);

      await errorsMutation.mutateAsync({
        client_ip: 0,
        user_id: userId,
        user_type_id: userTypeId,
        stack_trace: 'No Stack trace: Stripe Error while making a payment',
        message: e.message,
      });

      try {
        const parsedError = JSON.parse(e.message);
        if (parsedError?.message) {
          toast.error(parsedError.message);
        } else {
          toast.error('An error occurred with your payment method.');
        }
      } catch (parseError) {
        toast.error(e.message || 'An error occurred with your payment method.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { makeStripePayment, isLoading, isAlluraServerError };
};
